// Preguntas frecuentes de /ayuda (la pantalla está en Ayuda.js; aquí solo va el contenido).
//
// Para AGREGAR una pregunta copia un bloque { tema, pregunta, respuesta }, pégalo donde quieras que aparezca y cambia los textos.
// Para QUITARLA borra su bloque. En pantalla salen en este mismo orden, agrupadas por tema.
//  - tema: el `id` de uno de los TEMAS (dice en qué sección aparece). Si no coincide con ninguno, la pregunta sale en
//    "Otras preguntas" al final.
//  - pregunta: el texto de la pregunta. También es su enlace directo, sin acentos ni signos:
//    "¿Cómo contrato un servicio?" → /ayuda#como-contrato-un-servicio (si cambias el texto, cambia el enlace).
//  - respuesta: lista de párrafos. Cada párrafo es un texto, o una lista de textos [ '…', '…' ] si quieres viñetas.
//    Un enlace dentro de un texto se escribe [lo que se lee](/ruta), por ejemplo [Mis tickets](/user/tickets).
//    Los nombres de botones y pantallas van entre «comillas», tal como se ven en la web.

export const TEMAS = [
  { id: 'empezar', nombre: 'Primeros pasos' },
  { id: 'contratar', nombre: 'Contratar un servicio' },
  { id: 'pagos', nombre: 'Pagos y cancelaciones' },
  { id: 'ofrecer', nombre: 'Ofrecer servicios' },
  { id: 'cuenta', nombre: 'Cuenta y seguridad' },
  { id: 'mensajes', nombre: 'Mensajes y soporte' },
];

export const PREGUNTAS = [
  // ------------------------------------------------------------------ Primeros pasos
  {
    tema: 'empezar',
    pregunta: '¿Qué es OneService?',
    respuesta: [
      'OneService es una plataforma para encontrar, contratar y ofrecer servicios en México: clases, asesorías, servicios profesionales, reparaciones y mucho más. Buscas lo que necesitas, platicas con quien lo ofrece, reservas y pagas en un solo lugar.',
      'Con la misma cuenta puedes contratar servicios y también ofrecer los tuyos.',
    ],
  },
  {
    tema: 'empezar',
    pregunta: '¿Cuánto cuesta usar OneService?',
    respuesta: [
      'Crear tu cuenta, buscar y publicar servicios es gratis.',
      [
        'Si contratas, pagas solo el precio del servicio: no hay cargos adicionales para el cliente.',
        'Si ofreces servicios, OneService cobra una comisión del 10% por cada servicio que se paga en la plataforma: tú recibes el 90%. La comisión del procesador de pagos la cubre la plataforma.',
      ],
    ],
  },
  {
    tema: 'empezar',
    pregunta: '¿Necesito una cuenta?',
    respuesta: [
      'Para ver servicios, páginas y perfiles, no. Para escribirle a un proveedor, reservar, guardar favoritos o hablar con soporte, sí.',
      'Regístrate con tus datos y verifica tu cuenta con el código que te enviamos por correo o por SMS. También puedes entrar directamente con Google o Facebook.',
    ],
  },
  // El enlace «ayuda en línea» del paso del código del registro (Step3.js) abre esta pregunta: si cambias su texto, cambia también
  // el enlace allá.
  {
    tema: 'empezar',
    pregunta: 'No me llega el código de verificación, ¿qué hago?',
    respuesta: [
      'Al crear tu cuenta te lo mandamos al correo o al celular que elegiste en «Verifica tu cuenta». Si no te llega:',
      [
        'Si lo pediste por correo, búscalo también en la carpeta de correo no deseado o de promociones.',
        'Revisa que tu correo y tu número estén bien escritos. Si alguno está mal, regresa al primer paso y corrígelo.',
        'El código del correo vale 10 minutos y puedes intentarlo 5 veces. Si ya pasó el tiempo o se acabaron los intentos, regresa con la flecha y pide otro, por correo o por SMS.',
        'Si ya pediste varios códigos seguidos, espera 15 minutos antes de pedir otro.',
      ],
      'También puedes crear tu cuenta con Google o Facebook, sin código. Si aun así no te llega, escríbenos por WhatsApp desde el enlace al pie de esta página.',
    ],
  },
  {
    tema: 'empezar',
    pregunta: '¿Cómo encuentro el servicio que necesito?',
    respuesta: [
      'Usa el buscador de la parte de arriba («Buscar servicios») o explora el mapa de [Inicio](/). Abre cualquier servicio para ver su descripción, lo que incluye, el precio, las modalidades y las calificaciones de otros clientes.',
      'Si no encuentras exactamente lo que buscas, toca «Cotizar» en un servicio parecido y cuéntale al proveedor lo que necesitas.',
    ],
  },
  {
    tema: 'empezar',
    pregunta: '¿Cómo sé si puedo confiar en un proveedor?',
    respuesta: [
      'En su perfil verás cuántos servicios ha realizado en OneService, las evaluaciones que le han dado sus clientes y si su identidad está verificada.',
      [
        '«Identidad verificada» quiere decir que nuestro equipo revisó y aprobó su RFC y su CURP.',
        'Las calificaciones y los comentarios solo los pueden dejar clientes que completaron un servicio con esa persona.',
        'En categorías como Abogado o Médico, además validamos su cédula profesional antes de que el servicio se publique.',
      ],
    ],
  },

  // ------------------------------------------------------------------ Contratar un servicio
  {
    tema: 'contratar',
    pregunta: '¿Cómo contrato un servicio?',
    respuesta: [
      [
        'Abre el servicio y toca «Reservar».',
        'Elige la fecha, uno de los horarios disponibles, la modalidad y el idioma (si hay varios). Si quieres, déjale una nota al proveedor.',
        'Elige cómo pagar y confirma.',
      ],
      'Tu reserva queda como solicitud pendiente hasta que el proveedor la acepta. Te avisamos en tus notificaciones y por correo, y la puedes seguir en [Reservas](/user/reservas-proveedor).',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Qué significa que un servicio sea presencial, remoto o a domicilio?',
    respuesta: [
      [
        'Presencial: vas al lugar del proveedor.',
        'Remoto: el servicio se da en línea.',
        'A domicilio: el proveedor va a la dirección que elijas al reservar.',
      ],
      'Cada servicio indica qué modalidades ofrece, y tú eliges una al reservar.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Puedo pedir un precio o un servicio a mi medida?',
    respuesta: [
      'Sí. Toca «Cotizar» en el servicio, o en la página de un grupo o negocio, y cuéntale al proveedor lo que necesitas.',
      'El proveedor puede responderte con una cotización personalizada, con su precio, modalidad y condiciones. Ábrela desde la conversación en [Mensajes](/mensajes) y, si te convence, toca «Reservar ahora»: reservas con el precio acordado.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Qué pasa si el proveedor no responde mi solicitud?',
    respuesta: [
      'Si el proveedor no la acepta antes de la fecha y hora del servicio, la solicitud caduca: ya no se puede aceptar y no se te cobra nada.',
      'Mientras tanto puedes cancelarla sin costo desde [Reservas](/user/reservas-proveedor) y buscar otra opción.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Puedo cambiar la fecha o la hora de mi reserva?',
    respuesta: [
      'Sí, desde tu reserva en [Reservas](/user/reservas-proveedor):',
      [
        'Si el proveedor todavía no la acepta, toca «Modificar fecha de la reserva»: el cambio se aplica de inmediato.',
        'Si ya está confirmada, toca «Solicitar cambio de fecha»: el proveedor acepta o rechaza tu propuesta.',
      ],
      'El proveedor también puede proponerte otra fecha; te llegará para que la aceptes o la rechaces.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Para qué sirve el PIN de mi reserva?',
    respuesta: [
      'Cuando el proveedor acepta tu reserva, aparece en ella un PIN de confirmación de 4 dígitos que solo tú ves.',
      'Dáselo al proveedor únicamente cuando el servicio haya terminado y estés conforme: con ese PIN el proveedor lo marca como completado, y al compartirlo aceptas el servicio y sus extras.',
      'Si algo salió mal, no compartas el PIN: repórtalo desde tu reserva.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Qué son los cargos extra?',
    respuesta: [
      'Durante un servicio confirmado, el proveedor puede agregar cargos extra (por ejemplo, material adicional) con su concepto y su monto. Los ves en tu reserva al momento.',
      'Págalos desde tu reserva antes de dar tu PIN; se cobran con el mismo método de pago que usaste al reservar. Si no estás de acuerdo con un extra, platícalo antes de pagarlo: el proveedor puede quitarlo.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Cómo califico un servicio?',
    respuesta: [
      'Cuando el servicio se completa, puedes calificarlo de 1 a 5 estrellas y dejar un comentario desde [Reservas](/user/reservas-proveedor). Cada reserva se califica una sola vez.',
      'El proveedor también puede calificarte como cliente; esas opiniones aparecen en tu perfil de cliente.',
    ],
  },
  {
    tema: 'contratar',
    pregunta: '¿Puedo guardar servicios para verlos después?',
    respuesta: [
      'Sí. Toca el corazón en un servicio o en una página para agregarlo a tus favoritos. Los encuentras en el menú, en «Favoritos».',
    ],
  },

  // ------------------------------------------------------------------ Pagos y cancelaciones
  {
    tema: 'pagos',
    pregunta: '¿Qué métodos de pago aceptan?',
    respuesta: [
      'Tarjeta de débito o crédito, transferencia SPEI y efectivo en OXXO. En dispositivos compatibles también puedes pagar con Apple Pay o Google Pay.',
      'SPEI se puede elegir si el servicio es dentro de 24 horas o más, y OXXO si es dentro de 3 días o más y por hasta $10,000 (OXXO nos avisa del pago hasta el siguiente día hábil). Para antes, paga con tarjeta.',
      'Los pagos se procesan de forma cifrada con Stripe. OneService nunca guarda los datos de tu tarjeta.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Puedo pagarle directamente al proveedor?',
    respuesta: [
      'No. Todas las reservas se pagan en la plataforma, con tarjeta, SPEI u OXXO: así tu dinero queda protegido hasta que el servicio se completa. Un pago por fuera de OneService no lo podemos proteger ni devolver.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Cuándo se me cobra?',
    respuesta: [
      'Con tarjeta, tus datos se registran al reservar, pero el cargo se hace solo cuando el proveedor acepta tu reserva. Si no la acepta, no se te cobra nada.',
      'Con SPEI u OXXO, cuando el proveedor acepta te damos la CLABE y la referencia para transferir, o la ficha para pagar en tienda. Tienes hasta 72 horas, o hasta la hora del servicio si es antes, para pagar: si no llega el pago, la reserva se cancela sola. Tu PIN aparece cuando se confirma el pago.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Mi dinero está protegido?',
    respuesta: [
      'Sí. Lo que pagas queda en custodia de OneService; no se le entrega al proveedor en ese momento.',
      'Se libera cuando el servicio se completa (cuando le das tu PIN al proveedor, o automáticamente 3 días después del servicio si nadie reportó un problema), y aun así pasa unos días en un periodo de protección antes de llegarle al proveedor.',
      'Si algo sale mal, repórtalo desde tu reserva antes de que se complete: el dinero se congela hasta que nuestro equipo revise el caso.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Puedo cancelar una reserva y recuperar mi dinero?',
    respuesta: [
      'Sí, desde tu reserva en [Reservas](/user/reservas-proveedor). Según nuestra política de cancelación, depende de cuándo lo hagas:',
      [
        'Si el proveedor todavía no la acepta: es gratis, porque aún no se te cobra.',
        'Si ya está confirmada: es gratis durante la primera hora después de reservar, o mientras falten más de 24 horas para el servicio.',
        'Con menos de 24 horas: se retiene el 30% como compensación para el proveedor y se te devuelve el resto.',
        'Con menos de 3 horas: ya no se puede cancelar desde la plataforma; ponte de acuerdo con el proveedor.',
      ],
      'Si pagaste con tarjeta, el reembolso vuelve a la misma tarjeta y puede tardar algunos días en reflejarse, según tu banco. Si pagaste con SPEI u OXXO, nuestro equipo te hace la devolución por transferencia; mientras tanto verás «Devolución en proceso» en tu reserva.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Qué pasa si el proveedor rechaza o cancela mi reserva?',
    respuesta: [
      'Si rechaza tu solicitud, no se te cobra nada. Si cancela una reserva que ya pagaste, se te devuelve el total.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Puedo pedir factura?',
    respuesta: [
      'Sí. Al reservar, activa «¿Requieres factura?» y elige tus datos fiscales guardados o captura unos nuevos (RFC, razón social, régimen fiscal y uso de CFDI, entre otros).',
      'OneService emite tu factura y, cuando esté lista, puedes descargar el PDF y el XML desde tu reserva.',
    ],
  },
  {
    tema: 'pagos',
    pregunta: '¿Cómo uso un código promocional?',
    respuesta: [
      'Escríbelo en el resumen de tu reserva, antes de confirmar, y aplícalo. Algunos códigos solo valen para ciertas categorías o piden un monto mínimo; si el tuyo no aplica, te lo diremos ahí mismo.',
    ],
  },

  // ------------------------------------------------------------------ Ofrecer servicios
  {
    tema: 'ofrecer',
    pregunta: '¿Cómo publico un servicio?',
    respuesta: [
      'Entra a [Mis servicios](/user/servicios) y crea un servicio. Te pediremos el nombre, la descripción, lo que incluye, fotos, la categoría, las modalidades e idiomas, el precio y tu disponibilidad semanal (los horarios en que te pueden reservar).',
      'Puedes guardarlo como borrador y publicarlo cuando esté listo. Una vez publicado, la gente puede encontrarlo, cotizarlo y adquirirlo.',
      'Para recibir pagos en línea, conecta también tu cuenta en la pestaña «Datos para pagos» de tu [perfil](/mi-perfil).',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Qué es una página de servicios?',
    respuesta: [
      'Es un espacio con banner y descripción para presentar varios servicios juntos. Hay dos tipos:',
      [
        'Independiente: reúne tus propios servicios de una misma categoría.',
        'Grupo o negocio: para un equipo o un establecimiento. Cada servicio lo imparte una persona: tú o alguien a quien invites.',
      ],
      'Las creas en [Mis servicios](/user/servicios), en la pestaña «Página de servicios».',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Cómo invito a alguien a impartir un servicio en mi grupo o negocio?',
    respuesta: [
      'En tu página de grupo o negocio, elige en cada servicio quién lo imparte e invita a esa persona. Le llega una invitación, y el servicio no se muestra ni se puede contratar hasta que la acepte.',
      'Las reservas y los mensajes de ese servicio te siguen llegando a ti.',
      'Si a ti te invitaron, responde tus invitaciones en [Servicios que imparto](/user/colaboraciones).',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Cómo acepto o rechazo una reserva?',
    respuesta: [
      'Las solicitudes te llegan a [Reservas](/user/reservas-proveedor?vista=proveedor), en la vista «Proveedor»; también te avisamos en tus notificaciones y por correo.',
      'Al aceptar una solicitud se le cobra al cliente (o se le envía su referencia, si eligió SPEI u OXXO) y tu horario queda apartado. Si la rechazas, puedes indicar el motivo, y no se le cobra nada.',
      'Responde antes de la fecha y hora del servicio: después la solicitud caduca y ya no se puede aceptar.',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Cómo marco un servicio como completado?',
    respuesta: [
      'Al terminar, pídele al cliente su PIN de confirmación y escríbelo en la reserva. Si agregaste extras, el cliente tiene que pagarlos antes.',
      'Si el servicio sí se dio pero el cliente no te pasó el PIN, se completa automáticamente 3 días después de la fecha del servicio, siempre que nadie haya reportado un problema. Si hubo un problema, repórtalo desde la reserva.',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Cómo recibo mis pagos?',
    respuesta: [
      'En tu [perfil](/mi-perfil), pestaña «Datos para pagos», conecta tu cuenta con Stripe: ahí verificas tu identidad y registras tu cuenta bancaria de forma segura. OneService no guarda tus datos bancarios.',
      'Cuando un servicio se completa, tu parte (el 90%) queda retenida unos días como protección por si hay algún reporte, y se libera en el corte semanal. De Stripe pasa a tu banco según el calendario de Stripe.',
      'Si un cliente usa un código promocional, el descuento lo absorbe OneService: tú recibes tu 90% sobre el precio original.',
    ],
  },
  {
    tema: 'ofrecer',
    pregunta: '¿Por qué no aparece mi servicio?',
    respuesta: [
      'Revisa estos casos:',
      [
        'Está como borrador o inactivo: publícalo, o enciende su interruptor «Activo» en [Mis servicios](/user/servicios).',
        'Su categoría requiere cédula profesional y todavía no la aprobamos: súbela en la pestaña «Documentos oficiales» de tu [perfil](/mi-perfil).',
        'Está en una página de grupo o negocio y la persona invitada todavía no acepta impartirlo.',
        'Lo estás buscando con tu propia cuenta: tus servicios no te aparecen en el mapa ni en las búsquedas, para que nadie se contrate a sí mismo. Las demás personas sí los ven.',
      ],
    ],
  },

  // ------------------------------------------------------------------ Cuenta y seguridad
  {
    tema: 'cuenta',
    pregunta: 'Olvidé mi contraseña, ¿cómo la recupero?',
    respuesta: [
      'En la pantalla para iniciar sesión toca «Olvidé mi contraseña» y elige recibir un código por correo o por SMS. Con ese código creas una contraseña nueva, y por seguridad se cierran tus sesiones abiertas en otros dispositivos.',
      'La recuperación por SMS solo funciona si tu celular está confirmado.',
    ],
  },
  {
    tema: 'cuenta',
    pregunta: '¿Cómo cambio mi contraseña?',
    respuesta: [
      'En tu [perfil](/mi-perfil), pestaña «Info del perfil», edita tu «Contraseña»; te pediremos la actual. Al cambiarla se cierran tus sesiones en otros dispositivos y te avisamos por correo.',
      'Si entras con Google o Facebook y nunca creaste una contraseña, desde ahí mismo puedes crear la primera.',
    ],
  },
  {
    tema: 'cuenta',
    pregunta: 'Mi celular dice «Sin confirmar», ¿qué significa?',
    respuesta: [
      'Que todavía no comprobamos que el número es tuyo. Mientras tanto solo tú lo ves y no sirve para recuperar tu contraseña.',
      'Para confirmarlo, entra a tu [perfil](/mi-perfil), toca «Confirmar por SMS» y escribe el código que te llega.',
    ],
  },
  {
    tema: 'cuenta',
    pregunta: '¿Quién puede ver mi correo y mi celular?',
    respuesta: [
      'Tú decides. En tu [perfil](/mi-perfil), en «Compartir mi correo y celular con:», elige una opción:',
      [
        'Todos: cualquier persona que entre a tu página.',
        'Miembros: personas registradas en OneService.',
        'Conexiones: personas con las que has trabajado.',
        'Nadie: nadie verá tus datos de contacto.',
      ],
      'Un celular sin confirmar no se le muestra a nadie más.',
    ],
  },
  {
    tema: 'cuenta',
    pregunta: '¿Cómo elimino mi cuenta?',
    respuesta: [
      'En tu [perfil](/mi-perfil), pestaña «Info del perfil», toca «Eliminar mi cuenta». Es irreversible: se borran tus datos personales, pierdes el acceso y tus servicios y páginas se dan de baja.',
      'Si tienes reservas en curso, te recomendamos terminarlas o cancelarlas antes.',
    ],
  },

  // ------------------------------------------------------------------ Mensajes y soporte
  {
    tema: 'mensajes',
    pregunta: '¿Puedo mandar fotos o documentos por el chat?',
    respuesta: [
      'Sí: imágenes y archivos PDF, hasta 3 por mensaje y de hasta 5 MB cada uno.',
      'Los archivos se guardan 90 días; después se borran y el mensaje conserva su texto. Descarga lo que quieras conservar.',
    ],
  },
  {
    tema: 'mensajes',
    pregunta: '¿Cómo bloqueo o reporto a alguien?',
    respuesta: [
      'En la conversación, abre el menú (⋮) y elige «Bloquear a esta persona». Mientras dure el bloqueo no podrán escribirse; puedes quitarlo cuando quieras.',
      'Si alguien te molesta o te parece sospechoso, elige «Reportar conversación»: nuestro equipo recibe el reporte con los últimos mensajes y lo revisa.',
      'Por tu seguridad, nunca compartas tus contraseñas ni los códigos que te enviamos, y paga siempre dentro de OneService: así tu dinero queda protegido.',
    ],
  },
  {
    tema: 'mensajes',
    pregunta: 'Tuve un problema con un servicio, ¿qué hago?',
    respuesta: [
      'Repórtalo desde la reserva, en [Reservas](/user/reservas-proveedor):',
      [
        'Si el servicio todavía no se completa (por ejemplo, la otra persona no se presentó o el servicio no fue lo acordado), abre un reporte: el pago se congela hasta que nuestro equipo lo resuelva.',
        'Si el servicio ya se completó, abre una aclaración (un cobro que no reconoces, tu factura o la calidad del servicio) y nuestro equipo la revisa contigo.',
      ],
      'En los dos casos cuéntanos qué pasó y, si tienes, adjunta evidencia. Da seguimiento y responde al equipo desde [Mis tickets](/user/tickets).',
    ],
  },
  {
    tema: 'mensajes',
    pregunta: '¿Cómo contacto a soporte?',
    respuesta: [
      'Escríbenos en el [chat de soporte](/soporte) y nuestro equipo te responde ahí mismo. También puedes escribirnos por WhatsApp desde el enlace al pie de esta página.',
      'Tus reportes y aclaraciones, con las respuestas del equipo, quedan en [Mis tickets](/user/tickets).',
    ],
  },
];
